import { ContextMetadata, useCampaignStore } from '@lidea/shared/data-access/core';
import { plotKeys } from '@lidea/shared/data-access/plots';
import { Measures, ZoningStatus } from '@lidea/shared/util/types';
import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';

import { analysisKeys } from './../../keys/analysis-keys';
import { patchMeasureFields } from './apply-pending-measure-updates';
import { computeCornStatus } from './compute-corn-status';
import { computeSunflowerStatus } from './compute-sunflower-status';
import {
  measureUpdateRetry,
  updateMeasureMutationFn,
} from './update-measure-mutation';

export const updateMeasureDefaults = {
  mutationKey: ['updateMeasure'],
  mutationFn: updateMeasureMutationFn,
  retry: measureUpdateRetry,
  // Pause (do not fire PATCH) while app offline mode is active — manual HL or no network.
  networkMode: 'online' as const,
  scope: {
    id: 'measure',
  },
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface UseUpdateMeasureProps {
  analysisId: number;
  plotId: number;
}

export function useUpdateMeasure(props: UseUpdateMeasureProps) {
  const { t } = useTranslation(['analysis'], { useSuspense: false });
  const queryClient = useQueryClient();
  const campaignId = useCampaignStore((s) => s.campaignId);

  const mutation = useMutation({
    ...updateMeasureDefaults,
    onMutate: async ({ id, data }) => {
      // Await so in-flight refetches cannot overwrite the optimistic patch.
      await queryClient.cancelQueries({
        queryKey: analysisKeys.measures(props.analysisId).queryKey,
      });
      await queryClient.cancelQueries({
        queryKey: analysisKeys.zoning(props.analysisId).queryKey,
      });

      const previousMeasures = queryClient.getQueryData<Measures>(
        analysisKeys.measures(props.analysisId).queryKey
      );
      const previousZoning = queryClient.getQueryData(
        analysisKeys.zoning(props.analysisId).queryKey
      );
      if (!previousMeasures || !previousMeasures[id]) {
        return;
      }

      const previousMeasure = previousMeasures[id];
      const statics =
        previousMeasure.static?.reduce((acc, field) => {
          acc[field.name] = field.value;
          return acc;
        }, {} as Record<string, any>) || {};
      const dynamics = previousMeasure.dynamic.reduce((acc, dynamicGroup) => {
        dynamicGroup.fields.forEach((field) => {
          acc[field.name] = field.value;
        });
        return acc;
      }, {} as Record<string, any>);
      const values = {
        ...statics,
        ...dynamics,
        ...data,
      };

      let status = ZoningStatus.ToBeDone;
      if (previousZoning?.species_group?.name_fr === 'Mais') {
        status = computeCornStatus(values);
      }
      if (previousZoning?.species_group?.name_fr === 'Tournesol') {
        status = computeSunflowerStatus(values);
      }

      queryClient.setQueryData<Measures>(
        analysisKeys.measures(props.analysisId).queryKey,
        produce((draft) => {
          if (!draft?.[id]) return;
          draft[id].point.properties.status = status;
          patchMeasureFields(draft, id, data);
        })
      );

      const pointId = previousMeasures[id].point.id;
      if (previousZoning) {
        queryClient.setQueryData(
          analysisKeys.zoning(props.analysisId).queryKey,
          (oldData) => {
            if (!oldData) return oldData;
            return produce(oldData, (draft) => {
              draft.points.features.forEach((feature) => {
                if (feature.id === pointId) {
                  feature.properties.status = status;
                }
              });
            });
          }
        );
      }

      const metadata: ContextMetadata = {
        title: `${t('analysis:update_measure_mutation_title')} ${
          previousMeasures[id].point.properties.zone
        }`,
        fieldId: props.plotId,
        measureId: id,
        pointId: previousMeasures[id].point.id,
      };

      return { previousMeasures, previousZoning, metadata };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analysisKeys.measures(props.analysisId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: analysisKeys.zoning(props.analysisId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: plotKeys.detail(props.plotId, campaignId).queryKey,
      });
    },
    onError: (_, __, context) => {
      if (onlineManager.isOnline()) {
        // PATCH may have succeeded server-side despite a client-side error.
        queryClient.invalidateQueries({
          queryKey: analysisKeys.measures(props.analysisId).queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: analysisKeys.zoning(props.analysisId).queryKey,
        });
        return;
      }

      queryClient.setQueryData(
        analysisKeys.measures(props.analysisId).queryKey,
        context?.previousMeasures
      );
      queryClient.setQueryData(
        analysisKeys.zoning(props.analysisId).queryKey,
        context?.previousZoning
      );
    },
  });

  return mutation;
}

export default useUpdateMeasure;
